/**
 * PROHIBIDO MODIFICAR SIN AUTORIZACIÓN
 * 
 * Este componente es el centro de configuración del Agente Recepcionista.
 * Maneja la visualización dinámica de la Base de Conocimiento mediante pestañas
 * sincronizadas con los Programas Académicos.
 * 
 * Importancia: La lógica de parsing y reconstrucción de conocimiento (KnowledgeBase)
 * es compartida con el Super Agente. Cualquier cambio estructural romperá la IA.
 * 
 * Fecha: 2026-03-04
 * Autor: Trae AI
 */

import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  Box,
  TextField,
  Button,
  Grid,
  Select,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  InputAdornment,
  Badge,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@material-ui/core";
import {
  Delete,
  Add,
  VolumeUp,
  AttachFile,
  Save,
  Close,
  Description,
  Flare,
  Spellcheck,
  ExpandMore as ExpandMoreIcon
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import { getBackendUrl } from "../../config";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import ConfirmationModal from "../../components/ConfirmationModal";
import AcademicProgramsModal from "./AcademicProgramsModal";


/*
  CRITICAL - DO NOT MODIFY
  Este componente maneja la configuración avanzada del Agente CRM Ultron.
  Lógica blindada para:
  1. Gestión de pestañas (Tabs) mediante delimitadores robustos --- TAB: Nombre ---
  2. Persistencia de Material de Apoyo vinculada estrictamente por 'tabTitle'.
  3. Sincronización con Formik y validación de esquemas.
*/

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    backgroundColor: theme.palette.background.default,
    paddingTop: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 16,
    boxShadow: "none",
    border: `1px solid ${theme.palette.divider}`,
  },
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    "& h2": {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
  },
  knowledgeItem: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

const AgentSchema = Yup.object().shape({
  name: Yup.string().required("Requerido"),
  role: Yup.string().required("Requerido"),
  systemInstruction: Yup.string(),
  knowledgeBase: Yup.string(),
  supervisorPhone: Yup.string(),
  supervisorName: Yup.string(),
});

const VOICE_PRESETS = [
  { id: 'Zephyr', label: 'Marcela (Amable)' },
  { id: 'Kore', label: 'Elizabeth (Profesional)' },
  { id: 'Leda', label: 'Ingrid (Joven)' },
  { id: 'Callirrhoe', label: 'Leidy (Clara)' },
  { id: 'Phoebe', label: 'Valentina (Dulce)' },
  { id: 'Thalia', label: 'Camila (Dinámica)' },
  { id: 'Hestia', label: 'Elena (Maternal)' },
  { id: 'Selene', label: 'Diana (Solemne)' },
];

/**
 * Componente principal para la configuración avanzada de Agentes.
 * 
 * Este módulo centraliza la gestión de personalidad, voz y base de conocimiento del Agente CRM Ultron.
 * Utiliza un sistema de pestañas virtuales parseadas desde un único campo de texto en la base de datos
 * para mantener la compatibilidad con motores de IA que requieren un prompt plano.
 */
const Agente = () => {
  const classes = useStyles();
  const [agents, setAgents] = useState([]);
  const [voiceLoading, setVoiceLoading] = useState(false);

  // Knowledge Modal State
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const [knowledgeList, setKnowledgeList] = useState([]);
  const [newKnowledge, setNewKnowledge] = useState({
    trigger: "",
    type: "file",
    contentUrl: "",
    description: ""
  });
  const [uploading, setUploading] = useState(false);
  const [enhancing, setEnhancing] = useState(false);

  // Improvement Modal State
  const [improvementModalOpen, setImprovementModalOpen] = useState(false);
  const [improvementData, setImprovementData] = useState({ original: '', suggestion: '', callback: null });
  const [programsModalOpen, setProgramsModalOpen] = useState(false);

  const [programs, setPrograms] = useState([]);
  const [kbTabs, setKbTabs] = useState([{ title: "Principal", content: "" }]);
  const [activeKbTab, setActiveKbTab] = useState(0);
  const [kbMap, setKbMap] = useState({}); // Stores content indexed by title

  // Initial Load
  useEffect(() => {
    fetchAgents();
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const { data } = await api.get("/academic-programs");
      setPrograms(data.filter(p => p.status === 'active'));
    } catch (err) {
      console.error(err);
    }
  };

  const foundReceptionist = agents.find((a) => a.role === "receptionist");

  // Sync Tabs with Programs and Agent Data
  useEffect(() => {
    if (foundReceptionist) {
      console.log("[DEBUG] Syncing Agent Data & Programs");
      
      // 1. Parse current DB content into a map
      const currentContentMap = parseKnowledgeBaseToMap(foundReceptionist.knowledgeBase || "");
      setKbMap(currentContentMap);

      // 2. Build Tabs Structure: Principal + Active Programs
      const newTabs = [
        { 
          title: "Principal", 
          content: currentContentMap["Principal"] || "" 
        }
      ];

      programs.forEach(prog => {
        newTabs.push({
          title: prog.name,
          content: currentContentMap[prog.name] || ""
        });
      });

      setKbTabs(newTabs);

      // Sync Knowledge List (Media assets)
      if (foundReceptionist.knowledge) {
        setKnowledgeList(foundReceptionist.knowledge);
      } else {
        setKnowledgeList([]);
      }
    }
  }, [foundReceptionist?.id, foundReceptionist?.updatedAt, foundReceptionist?.knowledgeBase, programs.length]); 

  /**
   * Parses the flat text into a Key-Value map for easy access by Program Name.
   */
  const parseKnowledgeBaseToMap = (text) => {
    const map = {};
    if (!text) return map;

    const sections = text.split(/\n(?=--- TAB: )/g);
    sections.forEach((section) => {
      const trimmed = section.trim();
      if (!trimmed) return;
      
      const match = trimmed.match(/^--- TAB: (.*?) ---\n?([\s\S]*)$/);
      if (match) {
        map[match[1].trim()] = match[2] ? match[2].trim() : "";
      } else {
        // Content without header goes to Principal (or append if Principal exists)
        map["Principal"] = (map["Principal"] || "") + "\n" + trimmed;
      }
    });
    return map;
  };


  /**
   * Sincroniza el estado local de pestañas con el campo oculto de Formik.
   * 
   * Reconstruye el string único usando los delimitadores robustos antes de enviarlo al backend.
   * 
   * @param {Array} newTabs - Nuevo arreglo de pestañas.
   * @param {Function} setFieldValue - Función de Formik para actualizar valores.
   */
  const updateKbTabs = (newTabs, setFieldValue) => {
    setKbTabs(newTabs);
    
    const fullText = newTabs.map((tab) => {
        return `--- TAB: ${tab.title} ---\n${tab.content}`;
    }).join('\n\n');
    
    console.log("[DEBUG] Reconstructed KnowledgeBase for Save:", fullText);
    
    if (setFieldValue) {
        setFieldValue('knowledgeBase', fullText);
    }
  };


  const handleTabContentChange = (val, index, setFieldValue) => {
      const newTabs = [...kbTabs];
      newTabs[index].content = val;
      // We update state AND formik
      updateKbTabs(newTabs, setFieldValue);
  };

  const handleInsertText = (textToInsert, index, setFieldValue) => {
    const currentContent = kbTabs[index].content || "";
    const textField = document.getElementById(`kb-textfield-${index}`);
    
    let newContent = currentContent + textToInsert;
    let newCursorPos = newContent.length;

    if (textField) {
      const start = textField.selectionStart;
      const end = textField.selectionEnd;
      const textBefore = currentContent.substring(0, start);
      const textAfter = currentContent.substring(end, currentContent.length);
      
      newContent = textBefore + textToInsert + textAfter;
      newCursorPos = start + textToInsert.length;
      
      // If inserting [[ ]], place cursor inside
      if (textToInsert === "[[ ]]") {
        newCursorPos = start + 3; 
      }
    }

    handleTabContentChange(newContent, index, setFieldValue);
    
    // Restore focus and cursor position after render
    setTimeout(() => {
      if (textField) {
        textField.focus();
        textField.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const fetchAgents = async () => {
    try {
      const { data } = await api.get("/agents");
      setAgents(data);
    } catch (err) {
      toast.error("Error al cargar agentes");
    }
  };

  const previewVoice = async (voiceId) => {
    if (!voiceId) return;
    try {
      setVoiceLoading(true);
      const { data } = await api.post("/api/tts/preview", {
        presetId: voiceId,
        text: "¡Hola! Qué gusto saludarte. Desde ahora, con esta voz, te acompañaré en tus consultas de ventas. ¡Muchas gracias por escribirnos! ¿En qué puedo ayudarte hoy?"
      });
      if (data.ok && data.url) {
        const audio = new Audio(data.url);
        await audio.play();
      }
    } catch (err) {
      toast.error("Error al reproducir voz");
    } finally {
      setVoiceLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/agents/upload", formData);
      let backendUrl = getBackendUrl() || "http://localhost:4000";
      if (backendUrl.endsWith('/')) backendUrl = backendUrl.slice(0, -1);
      
      const publicUrl = `${backendUrl}/public/${data.filename}`;
      setNewKnowledge(prev => ({ ...prev, contentUrl: publicUrl }));
      toast.success("Archivo subido correctamente");
    } catch (err) {
      toast.error("Error al subir archivo");
    } finally {
      setUploading(false);
      e.target.value = null;
    }
  };

  /**
   * Agrega un nuevo ítem de Material de Apoyo (Archivo, Link, etc).
   * 
   * CRITICAL: Vincula el ítem con la pestaña activa mediante 'tabTitle' para asegurar la exclusividad.
   */
  const addKnowledgeItem = () => {
    const trigger = (newKnowledge.trigger || "").trim();
    const contentUrl = (newKnowledge.contentUrl || "").trim();
    const description = (newKnowledge.description || "").trim();

    if (!trigger || !contentUrl || !description) {
      toast.error("Por favor completa: Trigger, Subir Archivo y Descripción");
      return;
    }

    // Capturamos el título de la pestaña actual para la persistencia filtrada
    const currentTabTitle = kbTabs[activeKbTab]?.title || "Principal";
    console.log("[DEBUG] Adding Knowledge Item to Tab:", currentTabTitle);

    const newItem = { 
        trigger, 
        contentUrl, 
        description, 
        type: newKnowledge.type || "file",
        id: Date.now(),
        tabTitle: currentTabTitle // Atributo clave para el filtrado por pestaña
    };

    const updatedList = [...knowledgeList, newItem];
    
    console.log("[DEBUG] Updated Knowledge List:", updatedList);
    setKnowledgeList(updatedList);
    setNewKnowledge({ trigger: "", type: "file", contentUrl: "", description: "" });
    toast.success(`Agregado a la pestaña "${currentTabTitle}" (Guardar para persistir)`);
  };

  const removeKnowledgeItem = (id) => {
    setKnowledgeList(knowledgeList.filter(k => k.id !== id));
  };

  // Filter knowledgeList based on active tab for display in the Modal
  const activeKnowledgeItems = knowledgeList.filter(item => {
    const itemTab = (item.tabTitle || "Principal").trim().toLowerCase();
    const currentTab = (kbTabs[activeKbTab]?.title || "Principal").trim().toLowerCase();
    return itemTab === currentTab;
  });

  const handleEnhanceText = async (text, type, callback) => {
    if (!text) return;
    setEnhancing(true);
    try {
      const { data } = await api.post("/gemini/enhance", { text, type });
      if (type === 'spelling') {
        callback(data.text);
        toast.success("Ortografía corregida");
      } else {
        setImprovementData({ original: text, suggestion: data.text, callback });
        setImprovementModalOpen(true);
      }
    } catch (err) {
      toast.error("Error con IA");
    } finally {
      setEnhancing(false);
    }
  };

  const handleAcceptImprovement = () => {
    if (improvementData.callback) improvementData.callback(improvementData.suggestion);
    setImprovementModalOpen(false);
    toast.success("Mejora aplicada");
  };

  /**
   * Envía los datos actualizados del agente al servidor.
   * 
   * CRITICAL: Se eliminan los IDs temporales del Material de Apoyo antes del envío
   * para prevenir errores de desbordamiento (Integer Overflow) en la base de datos.
   * 
   * @param {Object} values - Valores recolectados por Formik.
   */
  const handleSave = async (values) => {
    try {
      // Mapeo explícito para limpiar datos y asegurar la consistencia del tabTitle
      const sanitizedKnowledge = knowledgeList.map(k => ({
        trigger: k.trigger,
        type: k.type,
        contentUrl: k.contentUrl,
        description: k.description,
        tabTitle: k.tabTitle || "Principal"
      }));

      console.log("[DEBUG] Saving Agent with Knowledge (Sanitized):", sanitizedKnowledge);

      const agentData = { ...values, knowledge: sanitizedKnowledge };

      /**
       * TODO: Integración con Lógica de Laboratorio
       * Aquí se conectará la lógica para que, si la IA falla, envíe un mensaje
       * al número almacenado en agentData.supervisorPhone.
       * 
       * Formato sugerido: "Jefe, hay un cliente llamado [nombre] con el teléfono [tel] preguntando: [duda]"
       */

      if (values.id) {
        await api.put(`/agents/${values.id}`, agentData);
        toast.success("Agente actualizado correctamente");
      } else {
        await api.post("/agents", agentData);
        toast.success("Agente creado correctamente");
      }
      fetchAgents();
    } catch (err) {
      console.error("[ERROR] Saving agent:", err);
      toast.error("Error al guardar en el servidor");
    }
  };

  const EnhanceButtons = ({ text, onChange }) => (
    <InputAdornment position="end">
      <Tooltip title="Ortografía (IA)">
        <IconButton size="small" onClick={() => handleEnhanceText(text, 'spelling', onChange)} disabled={enhancing || !text}>
          <Spellcheck fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Mejorar (IA)">
        <IconButton size="small" onClick={() => handleEnhanceText(text, 'improvement', onChange)} disabled={enhancing || !text} color="primary">
          <Flare fontSize="small" />
        </IconButton>
      </Tooltip>
    </InputAdornment>
  );

  const InsertHelpers = ({ onInsert, onOpenDialog }) => (
    <InputAdornment position="end">
      <Tooltip title="Insertar Título (#)">
        <IconButton size="small" onClick={() => onInsert("# ")} style={{ fontWeight: 'bold', fontSize: '1rem' }}>
          #
        </IconButton>
      </Tooltip>
      <Tooltip title="Invocar Material ([[ ]])">
        <IconButton size="small" onClick={onOpenDialog} style={{ fontWeight: 'bold', fontSize: '1rem' }}>
          [[ ]]
        </IconButton>
      </Tooltip>
    </InputAdornment>
  );

  const [kbDialogOpen, setKbDialogOpen] = useState(false);

  const handleOpenKbDialog = () => {
    setKbDialogOpen(true);
  };

  const handleCloseKbDialog = () => {
    setKbDialogOpen(false);
  };

  const handleSelectKnowledge = (trigger, index, setFieldValue) => {
    handleInsertText(`[[${trigger}]]`, index, setFieldValue);
    handleCloseKbDialog();
  };

  const receptionistValues = foundReceptionist ? {
    ...foundReceptionist,
    systemInstruction: foundReceptionist.systemInstruction || "",
    knowledgeBase: foundReceptionist.knowledgeBase || "",
    supervisorPhone: foundReceptionist.supervisorPhone || "",
    supervisorName: foundReceptionist.supervisorName || "",
  } : {
    name: "Recepcionista",
    role: "receptionist",
    prompt: "",
    systemInstruction: "",
    knowledgeBase: "",
    supervisorPhone: "",
    supervisorName: "",
    voiceId: "",
    voiceStyle: "",
    isActive: true,
  };

  return (
    <div className={classes.root}>
      <Dialog open={improvementModalOpen} onClose={() => setImprovementModalOpen(false)} maxWidth="sm" fullWidth>

        <DialogTitle style={{ backgroundColor: '#3f51b5', color: 'white' }}>Mejora de IA</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2">Original:</Typography>
          <Paper variant="outlined" style={{ padding: 10, backgroundColor: '#f5f5f5', marginBottom: 20 }}>
            <Typography variant="body2">{improvementData.original}</Typography>
          </Paper>
          <Typography variant="subtitle2" color="primary">Sugerencia:</Typography>
          <Paper variant="outlined" style={{ padding: 15, backgroundColor: '#e3f2fd', borderColor: '#2196f3' }}>
            <Typography variant="body1">{improvementData.suggestion}</Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImprovementModalOpen(false)}>Cancelar</Button>
          <Button onClick={handleAcceptImprovement} color="primary" variant="contained">Aplicar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={knowledgeOpen} onClose={() => setKnowledgeOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle className={classes.dialogTitle}>Gestionar Material de apoyo
          <IconButton onClick={() => setKnowledgeOpen(false)} style={{ color: 'white' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600 }}>Nuevo Activo</Typography>
              <Paper variant="outlined" style={{ padding: 16, backgroundColor: 'transparent' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel style={{ color: "var(--color-text)" }}>Tipo</InputLabel>
                      <Select 
                        value={newKnowledge.type} 
                        onChange={(e) => setNewKnowledge({ ...newKnowledge, type: e.target.value })} 
                        label="Tipo"
                        style={{ color: "var(--color-text)" }}
                      >
                        <MenuItem value="file">Archivo (PDF)</MenuItem>
                        <MenuItem value="photo">Foto (JPG/PNG)</MenuItem>
                        <MenuItem value="video">Video (MP4)</MenuItem>
                        <MenuItem value="link">Enlace (URL)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <TextField 
                      fullWidth 
                      variant="outlined" 
                      size="small" 
                      label="Disparador (Trigger)" 
                      placeholder="Ej: legal, precios, sedes" 
                      value={newKnowledge.trigger} 
                      onChange={(e) => setNewKnowledge({ ...newKnowledge, trigger: e.target.value })} 
                      InputLabelProps={{ style: { color: "var(--color-text)" } }}
                      InputProps={{ style: { color: "var(--color-text)", backgroundColor: "transparent" } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center">
                      <Button variant="outlined" component="label" disabled={uploading} color="primary" startIcon={<AttachFile />}>
                        {uploading ? "Subiendo..." : "Seleccionar Archivo"}
                        <input type="file" hidden onChange={handleFileUpload} />
                      </Button>
                      <TextField 
                        fullWidth 
                        variant="outlined" 
                        size="small" 
                        disabled 
                        value={newKnowledge.contentUrl} 
                        placeholder="URL del archivo" 
                        style={{ marginLeft: 16 }} 
                        InputLabelProps={{ style: { color: "var(--color-text)" } }}
                        InputProps={{ style: { color: "var(--color-text)", backgroundColor: "transparent" } }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField 
                      fullWidth 
                      multiline 
                      rows={2} 
                      label="Descripción (Instrucción para el Agente)" 
                      placeholder="Ej: Usa este PDF cuando el cliente acepte recibir la resolución legal..." 
                      value={newKnowledge.description} 
                      onChange={(e) => setNewKnowledge({ ...newKnowledge, description: e.target.value })} 
                      InputLabelProps={{ style: { color: "var(--color-text)" } }}
                      InputProps={{
                        style: { color: "var(--color-text)", backgroundColor: "transparent" },
                        endAdornment: (
                          <EnhanceButtons 
                            text={newKnowledge.description} 
                            onChange={(val) => setNewKnowledge({ ...newKnowledge, description: val })} 
                          />
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} style={{ textAlign: 'right' }}>
                    <Button variant="contained" color="primary" onClick={addKnowledgeItem} startIcon={<Add />}>Agregar a la lista</Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600 }}>Activos Cargados ({activeKnowledgeItems.length})</Typography>
              <List>
                {activeKnowledgeItems.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" align="center" style={{ padding: 20 }}>No hay material cargado para esta sección.</Typography>
                ) : (
                  activeKnowledgeItems.map((k, index) => (
                    <ListItem key={k.id || index} className={classes.knowledgeItem}>
                      <ListItemText 
                        primary={<Typography variant="subtitle2" color="primary">{`[[Trigger: ${k.trigger}]] — ${k.type.toUpperCase()}`}</Typography>} 
                        secondary={k.description} 
                      />
                      <ListItemSecondaryAction>
                        <IconButton onClick={() => removeKnowledgeItem(k.id)} style={{ color: '#e91e63' }}>
                          <Delete />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))
                )}
              </List>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKnowledgeOpen(false)} variant="contained" color="primary">Listo, Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Container maxWidth="lg">
        <Paper className={classes.paper}>
          <Box position="relative" mb={2}>
            <Typography component="h2" variant="h5" color="primary" align="center" style={{ fontWeight: 600 }}>
              Configuración de Agente Crm Ultron
            </Typography>
            <Box position="absolute" right={0} top={0}>
              <Button variant="outlined" color="primary" onClick={() => setProgramsModalOpen(true)}>
                Programas
              </Button>
            </Box>
          </Box>
          <Box p={1}>
            <Formik enableReinitialize initialValues={receptionistValues} validationSchema={AgentSchema} onSubmit={handleSave}>
              {({ values, handleChange, setFieldValue }) => (
                <Form>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Accordion defaultExpanded elevation={0} style={{ border: '1px solid #e0e0e0' }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="h6">Información Básica</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <TextField fullWidth variant="outlined" label="Nombre" name="name" value={values.name} onChange={handleChange} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Box display="flex" alignItems="center">
                                <FormControl fullWidth variant="outlined">
                                  <InputLabel>Voz</InputLabel>
                                  <Select name="voiceId" value={values.voiceId || ""} onChange={handleChange} label="Voz">
                                    {VOICE_PRESETS.map((v) => <MenuItem key={v.id} value={v.id}>{v.label}</MenuItem>)}
                                  </Select>
                                </FormControl>
                                <IconButton onClick={() => previewVoice(values.voiceId)} disabled={voiceLoading} color="primary"><VolumeUp /></IconButton>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField 
                                fullWidth 
                                variant="outlined" 
                                label="Nombre del Jefe" 
                                name="supervisorName" 
                                value={values.supervisorName} 
                                onChange={handleChange} 
                                placeholder="Ej: Ing. Juan Pérez"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField 
                                fullWidth 
                                variant="outlined" 
                                label="Teléfono del Jefe (Escalamiento)" 
                                name="supervisorPhone" 
                                value={values.supervisorPhone} 
                                onChange={handleChange} 
                                placeholder="Ej: 573001234567"
                                helperText="Número al que la IA notificará si no encuentra respuesta en la Base de Conocimiento."
                              />
                            </Grid>
                            <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <Button type="submit" variant="contained" color="primary" startIcon={<Save />}>
                                Guardar Información Básica
                              </Button>
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                    <Grid item xs={12}>
                      <Accordion defaultExpanded elevation={0} style={{ border: '1px solid #e0e0e0' }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="h6">Base de Conocimiento</Typography></AccordionSummary>
                        <AccordionDetails style={{ flexDirection: 'column' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0}>
                            <Tabs 
                              value={activeKbTab} 
                              onChange={(e, v) => setActiveKbTab(v)} 
                              indicatorColor="primary" 
                              textColor="primary" 
                              variant="scrollable" 
                              style={{ flexGrow: 1 }}
                            >
                              {kbTabs.map((t, i) => (
                                <Tab key={i} label={t.title} />
                              ))}
                            </Tabs>
                          </Box>

                          <Box p={2} border={1} borderColor="grey.300" borderRadius="0 0 4px 4px" borderTop={0} position="relative" style={{ backgroundColor: '#ffffff', color: '#1a1a2e' }}>
                            {kbTabs[activeKbTab] && (
                              <TextField 
                                  key={activeKbTab}
                                  id={`kb-textfield-${activeKbTab}`}
                                  fullWidth 
                                  multiline 
                                  minRows={12} 
                                  label={kbTabs[activeKbTab].title} 
                                  value={kbTabs[activeKbTab].content} 
                                  onChange={(e) => handleTabContentChange(e.target.value, activeKbTab, setFieldValue)} 
                                  variant="outlined"
                                  inputProps={{
                                    style: {
                                      color: '#1a1a2e',
                                    }
                                  }}
                                  InputProps={{ 
                                    style: { backgroundColor: '#ffffff' },
                                    endAdornment: (
                                      <>
                                        {kbTabs[activeKbTab].title !== "Principal" && (
                                          <Box 
                                            position="absolute" 
                                            top={8} 
                                            right={16} 
                                            zIndex={10}
                                          >
                                            <Button 
                                              variant="contained" 
                                              style={{ backgroundColor: '#d32f2f', color: 'white', fontWeight: 600, textTransform: 'none' }}
                                              startIcon={<Description />} 
                                              onClick={() => {
                                                console.log("[DEBUG] Opening Knowledge Modal for tab:", kbTabs[activeKbTab].title);
                                                console.log("[DEBUG] Current knowledgeList:", knowledgeList);
                                                console.log("[DEBUG] Active items for this tab:", activeKnowledgeItems);
                                                setKnowledgeOpen(true);
                                              }} 
                                              size="small"
                                            >
                                              Material de apoyo: {kbTabs[activeKbTab].title} ({activeKnowledgeItems.length})
                                            </Button>
                                          </Box>
                                        )}
                                        <div style={{ position: 'absolute', bottom: 10, right: 10, zIndex: 10 }}>
                                           <InsertHelpers 
                                              onInsert={(text) => handleInsertText(text, activeKbTab, setFieldValue)} 
                                              onOpenDialog={handleOpenKbDialog}
                                           />
                                           <Dialog open={kbDialogOpen} onClose={handleCloseKbDialog} maxWidth="sm" fullWidth>
                                            <DialogTitle className={classes.dialogTitle}>
                                              <span>Seleccionar Material de Apoyo</span>
                                              <IconButton size="small" onClick={handleCloseKbDialog} style={{ color: '#fff' }}>
                                                <Close />
                                              </IconButton>
                                            </DialogTitle>
                                            <DialogContent dividers>
                                              <List>
                                                {activeKnowledgeItems.length === 0 ? (
                                                  <Typography variant="body2" color="textSecondary" align="center" style={{ padding: 20 }}>
                                                    No hay material disponible para esta sección.
                                                  </Typography>
                                                ) : (
                                                  activeKnowledgeItems.map((item, idx) => (
                                                    <ListItem 
                                                      button 
                                                      key={idx} 
                                                      onClick={() => handleSelectKnowledge(item.trigger, activeKbTab, setFieldValue)}
                                                      style={{ borderRadius: 8, marginBottom: 8, border: '1px solid #eee' }}
                                                    >
                                                      <ListItemText 
                                                        primary={<Typography variant="subtitle1" style={{ fontWeight: 600, color: '#d32f2f' }}>[[{item.trigger}]]</Typography>}
                                                        secondary={item.description || "Sin descripción"} 
                                                      />
                                                      <Typography variant="caption" color="textSecondary">
                                                        {item.type.toUpperCase()}
                                                      </Typography>
                                                    </ListItem>
                                                  ))
                                                )}
                                              </List>
                                            </DialogContent>
                                            <DialogActions>
                                              <Button onClick={handleCloseKbDialog} color="primary">Cerrar</Button>
                                            </DialogActions>
                                          </Dialog>
                                        </div>
                                      </>
                                    )
                                  }} 
                                />
                            )}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                    <Grid item xs={12} style={{ textAlign: 'right' }}><Button type="submit" variant="contained" color="primary" size="large" startIcon={<Save />}>Guardar Cambios</Button></Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </Box>
        </Paper>
      </Container>
      
      <AcademicProgramsModal open={programsModalOpen} onClose={() => setProgramsModalOpen(false)} />
    </div>
  );
};

export default Agente;
